
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentDashboardData } from '../../../../redux/reducers/paymentDashboardSlice';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 'none',
    fontSize: theme.typography.pxToRem(12),
    border: 'none',
    padding: 0,
    boxShadow: 'none',
  },
}));

const nameExpan = {
  week: 'Week',
  month: 'Month',
  year: 'Year',
}

function SummaryCards() {
  const dispatch = useDispatch();
  const { totalSale, totalPurchase, unpaidCount, unpaidTotal, loading, collectionsHealth } = useSelector(
    (state) => state.paymentDashboard
  );
  const [durationType, setDurationType] = useState('week');

  const formatNumber = (num) => {
    const value = Number(num);
    return value.toFixed(2);
  };

  useEffect(() => {
    dispatch(fetchPaymentDashboardData());
  }, [dispatch]);

  const getTooltipContent = (cardTitle, collectionsHealth) => {
    const reqData = collectionsHealth?.comparisonData?.[durationType]
    return (
      <div>
        <div
          className="apexcharts-tooltip-title"
          style={{
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontSize: '12px',
            backgroundColor: '#EBEBEB',
            color: '#000',
            padding: '8px',
            margin: 0,
          }}
        >
          {cardTitle}
        </div>
        <div
          className="apexcharts-tooltip-series-group"
          style={{
            order: 1,
            display: 'flex',
            backgroundColor: '#fff',
            padding: '8px',
          }}
        >
          <div
            className="apexcharts-tooltip-text"
            style={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '12px',
              color: '#000',
            }}
          >
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Current {nameExpan?.[durationType]}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Collections:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {formatNumber(reqData?.current?.collections ?? 0)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Sales:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {formatNumber(reqData?.current?.sales ?? 0)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Collection Percentage:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {reqData?.current?.collectionPercentage}%
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Last {nameExpan?.[durationType]}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Collections:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {formatNumber(reqData?.last?.collections ?? 0)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Sales:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {formatNumber(reqData?.last?.sales ?? 0)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Collection Percentage:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {reqData?.last?.collectionPercentage}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const cards = [
    { title: 'Total Sales', value: formatNumber(collectionsHealth?.totalSales ?? 0) },
    // { title: 'Total Purchases', value: formatNumber(totalPurchase) },
    // { title: 'Unpaid Invoices', value: formatNumber(unpaidCount) },
    // { title: 'Unpaid Total', value: formatNumber(unpaidTotal) },
    { title: 'Total Outstanding ', value: formatNumber(collectionsHealth?.outstanding ?? 0) },
  ];

  if (loading && !totalSale && !unpaidCount) {
    return <p>Loading...</p>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ToggleButtonGroup
          value={durationType}
          exclusive
          onChange={(_, val) => val && setDurationType(val)}
          size="small"
          sx={{
            backgroundColor: "#f0f2f5",
            borderRadius: "12px",

            // Styles for all grouped toggle buttons
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: "7px",
              borderColor: "transparent",
              color: "#333",
              transition: "none", // remove hover effect if needed

              // Only apply to selected button
              "&.Mui-selected": {
                backgroundColor: "#005EB8",
                color: "#fff",
              },
            },
          }}
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
        {cards.map((card, i) => {
          const tooltipContent = getTooltipContent(card.title, collectionsHealth);
          return (
            <HtmlTooltip
              key={i}
              title={tooltipContent}
              arrow
              followCursor
              placement="top"
            >
              <div
                style={{
                  padding: '1px 20px 20px 20px',
                  background: '#e9f2ff',
                  borderRadius: '16px',
                  flex: 1,
                }}
              >
                <h4>{card.title}</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ margin: 0 }}>{card.value}</h2>
                </div>
              </div>
            </HtmlTooltip>
          );
        })}
      </div>
    </Box>
  );
}

export default SummaryCards;